import ApiService from '@/services/ApiService'
import {
  applyRegionBaseUrls,
  filterFlag,
  resetRegionBaseUrls,
  regionBaseUrls
} from '@/const'

export const namespaced = true

const REGION_STORAGE_KEY = 'preferredRegion'
const AVAILABLE_REGIONS_STORAGE_KEY = 'availableRegions'

const normalizeRegionCode = region =>
  typeof region === 'string' ? region.trim().toLowerCase() : ''

const readStoredRegions = () => {
  try {
    return JSON.parse(localStorage.getItem(AVAILABLE_REGIONS_STORAGE_KEY)) || []
  } catch (error) {
    return []
  }
}

const normalizeRegions = regions => {
  if (!Array.isArray(regions)) return []

  return regions
    .map(region => ({
      code: normalizeRegionCode(region.code || region.Code),
      name: region.name || region.Name || region.code || region.Code
    }))
    .filter(region => region.code && regionBaseUrls[region.code])
}

const getResponseData = response => {
  const payload = response && response.data ? response.data : {}
  if (payload.success === false) {
    throw new Error(payload.msg || 'Unable to load region information')
  }
  return payload.data || {}
}

const canCreateOrganisation = response => {
  const payload = response && response.data ? response.data : {}
  return payload.errorcode === 804
}

export const state = {
  currentRegion: normalizeRegionCode(localStorage.getItem(REGION_STORAGE_KEY)),
  availableRegions: readStoredRegions(),
  switchStatus: 'idle'
}

export const mutations = {
  SET_CURRENT_REGION(state, region) {
    state.currentRegion = normalizeRegionCode(region)
    if (state.currentRegion) {
      localStorage.setItem(REGION_STORAGE_KEY, state.currentRegion)
    } else {
      localStorage.removeItem(REGION_STORAGE_KEY)
    }
  },
  SET_ACTIVE_REGION(state, region) {
    state.currentRegion = normalizeRegionCode(region)
  },
  SET_AVAILABLE_REGIONS(state, regions) {
    state.availableRegions = normalizeRegions(regions)
    localStorage.setItem(
      AVAILABLE_REGIONS_STORAGE_KEY,
      JSON.stringify(state.availableRegions)
    )
  },
  CLEAR_AVAILABLE_REGIONS(state) {
    state.availableRegions = []
    localStorage.removeItem(AVAILABLE_REGIONS_STORAGE_KEY)
  },
  SET_SWITCH_STATUS(state, status) {
    state.switchStatus = status
  }
}

export const getters = {}

export const actions = {
  useRegionApi({ commit }, region) {
    const normalizedRegion = normalizeRegionCode(region) || 'eu'
    if (!applyRegionBaseUrls(normalizedRegion)) {
      return Promise.reject(new Error('The invitation region is not supported'))
    }
    commit('SET_ACTIVE_REGION', normalizedRegion)
    return Promise.resolve(normalizedRegion)
  },
  restorePreferredRegionApi({ commit }) {
    const preferredRegion = normalizeRegionCode(
      localStorage.getItem(REGION_STORAGE_KEY)
    )
    if (preferredRegion && applyRegionBaseUrls(preferredRegion)) {
      commit('SET_ACTIVE_REGION', preferredRegion)
    } else {
      resetRegionBaseUrls()
      commit('SET_ACTIVE_REGION', '')
    }
  },
  restoreRegion({ commit, state }) {
    if (
      !localStorage.getItem('tokenInfo') ||
      !state.currentRegion ||
      !applyRegionBaseUrls(state.currentRegion)
    ) {
      resetRegionBaseUrls()
      commit('SET_CURRENT_REGION', '')
    }
  },
  fetchAvailableRegions({ commit }) {
    return ApiService.VerifyUserRegion().then(response => {
      const data = getResponseData(response)
      const availableRegions =
        data.AvailableRegions || data.availableRegions || []
      const preferredRegion = normalizeRegionCode(
        data.PrefferedRegion ||
          data.prefferedRegion ||
          data.PreferredRegion ||
          data.preferredRegion
      )

      commit('SET_AVAILABLE_REGIONS', availableRegions)

      return { preferredRegion }
    })
  },
  verifyUserRegion({ commit, dispatch }) {
    return dispatch('fetchAvailableRegions').then(result => {
      const preferredRegion = result.preferredRegion

      if (!preferredRegion) {
        resetRegionBaseUrls()
        commit('SET_CURRENT_REGION', '')
      } else if (!applyRegionBaseUrls(preferredRegion)) {
        throw new Error('The preferred region is not supported')
      } else {
        commit('SET_CURRENT_REGION', preferredRegion)
      }

      return result
    })
  },
  updatePreferredRegion({ commit }, region) {
    const normalizedRegion = normalizeRegionCode(region)
    if (!regionBaseUrls[normalizedRegion]) {
      return Promise.reject(new Error('The selected region is not supported'))
    }

    return ApiService.UpdatePreferredRegion(normalizedRegion).then(response => {
      getResponseData(response)
      applyRegionBaseUrls(normalizedRegion)
      commit('SET_CURRENT_REGION', normalizedRegion)
      return response
    })
  },
  prepareRegionSwitch({ commit, dispatch }) {
    commit('SET_SWITCH_STATUS', 'loading-organisations')
    dispatch(
      'user/clearOrganisationSelection',
      { clearOrganisations: true },
      { root: true }
    )
    commit('patient/SET_PATIENTS', [], { root: true })
    commit('patient/SET_SELECTED_PATIENT', '', { root: true })
    commit('patient/SET_SELECTED_PATIENT_GOAL', {}, { root: true })
    commit('patient/SET_SELECTED_PATIENT_DATA', {}, { root: true })
    commit('filter/SET_AREA_FILTERS', [], { root: true })
    commit('filter/SET_BUILDINGS', [], { root: true })
    commit(
      'filter/SET_DASHBOARD_FILTER',
      {
        organisationId: '',
        buildingId: '',
        flag: filterFlag.all,
        filterIds: []
      },
      { root: true }
    )

    return dispatch(
      'user/verifyUser',
      { assignSingleOrganisation: false },
      { root: true }
    )
      .then(response => {
        if (!response.data.success && !canCreateOrganisation(response)) {
          throw new Error(response.data.msg)
        }
        return {
          response,
          canCreateOrganisation: canCreateOrganisation(response)
        }
      })
      .catch(error => {
        commit('SET_SWITCH_STATUS', 'failed')
        throw error
      })
  },
  completeRegionSwitch({ commit }) {
    commit('SET_SWITCH_STATUS', 'idle')
  },
  clearRegion({ commit }) {
    resetRegionBaseUrls()
    commit('SET_CURRENT_REGION', '')
    commit('CLEAR_AVAILABLE_REGIONS')
    commit('SET_SWITCH_STATUS', 'idle')
  }
}
